var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { action, computed, makeObservable, observable, reaction } from 'mobx';
import { URLData } from '@telia-common-components/shared';
import API, { DEFAULT_TIMEOUT } from '../../../util/ApiUtil';
import ErrorUtil from '../../../util/ErrorUtil';
import { FetchStatus } from '../../../util/FetchStatus';
import ShopAPI from '../../../util/ShopApiUtil';
import { validateImei } from '../../../util/TextUtil';
import { Condition, ValidationError, } from '../src/TradeInInterfaces';
const LAPTOP_CATEGORY_ID = 12;
const PHONE_CATEGORY_ID = 1;
const TABLET_CATEGORY_ID = 3;
export const APPLE_ID = 116;
export const SMARTWATCH_CATEGORY_ID = 4;
const PHONE_AND_TABLET_INITIAL_CONDITION_OPTIONS = [
    Condition.NOT_TURNING_ON_OR_CHARGING,
    Condition.SCREEN_COLORS_OR_TOUCH_ISSUE,
    Condition.SCREEN_GLASS_CRACKED,
    Condition.FRAME_DAMAGED,
    Condition.BUTTONS_ISSUE,
];
const LAPTOP_INITIAL_CONDITION_OPTIONS = [
    Condition.LAPTOP_NO_POWER,
    Condition.LAPTOP_SCREEN_DAMAGE,
    Condition.LAPTOP_BOOT_FAILS,
    Condition.LAPTOP_PHYSICAL_DAMAGE,
];
const SMARTWATCH_INITIAL_CONDITION_OPTIONS = [Condition.SMARTWATCH_POWER_OR_SCREEN_DAMAGE, Condition.SMARTWATCH_CHARGER_AND_BAND];
export const isDeviceWithImei = (categoryId) => {
    return isPhoneOrTablet(categoryId) || categoryId === SMARTWATCH_CATEGORY_ID;
};
export const isPhoneOrTablet = (categoryId) => {
    return [PHONE_CATEGORY_ID, TABLET_CATEGORY_ID].includes(categoryId);
};
export const isLaptopOrTabletOrWatch = (categoryId) => {
    return [LAPTOP_CATEGORY_ID, TABLET_CATEGORY_ID, SMARTWATCH_CATEGORY_ID].includes(categoryId);
};
const formatOptions = ({ id, name }) => ({
    label: name,
    id: String(id),
    url: '#',
});
export class TradeInStore {
    constructor(rootStore, props) {
        this.tradeInResponseHolder = {
            cart: null,
            error: null,
            status: FetchStatus.UNINITIALIZED,
        };
        this.manufacturersHolder = {
            status: FetchStatus.UNINITIALIZED,
            manufacturers: new Map(),
        };
        this.devicesHolder = {
            status: FetchStatus.UNINITIALIZED,
            devices: new Map(),
        };
        this.categoriesHolder = {
            status: FetchStatus.UNINITIALIZED,
            categories: null,
        };
        this.selected = {
            manufacturerId: null,
        };
        this.maximumPrice = {
            status: FetchStatus.UNINITIALIZED,
            response: null,
            error: null,
        };
        this.priceInfo = {
            status: FetchStatus.UNINITIALIZED,
            response: null,
            error: null,
        };
        this.imeiValidation = {
            status: FetchStatus.UNINITIALIZED,
            response: null,
            error: null,
        };
        this.formErrors = new Map();
        this.terms = false;
        this.loading = false;
        this.formData = {
            deviceId: null,
            categoryId: null,
            conditions: PHONE_AND_TABLET_INITIAL_CONDITION_OPTIONS,
            imeiOrSn: null,
            lang: null,
        };
        this.manufacturersSearchString = '';
        this.devicesSearchString = '';
        this.imeiFocused = false;
        this.dataConfirmationChecked = false;
        this.correctDeviceConfirmationChecked = false;
        this.deviceLockConfirmed = false;
        this.answeredQuestions = new Set();
        this.questionsClosed = new Set();
        this.promotedManufacturers = {};
        this.finalPrice = 0;
        this.previousImeiOrSn = '';
        this.modalOpen = false;
        this.reactions = [];
        this.disposeReactions = () => {
            this.reactions.splice(0, this.reactions.length).forEach((reactionDisposer) => reactionDisposer());
        };
        this.setPromotedManufacturers = (manufacturers) => {
            this.promotedManufacturers = manufacturers;
        };
        this.setDataConfirmationChecked = (val) => {
            this.dataConfirmationChecked = val;
        };
        this.setCorrectDeviceConfirmationChecked = (val) => {
            this.correctDeviceConfirmationChecked = val;
        };
        this.setDeviceLockConfirmation = (val) => {
            this.deviceLockConfirmed = val;
        };
        this.setModalOpen = (isOpen) => {
            this.modalOpen = isOpen;
        };
        this.setTranslate = (translate) => {
            this.translate = translate;
        };
        this.onImeiOrSnChanged = async () => {
            var _a;
            if (!!this.previousImeiOrSn && this.hasAnsweredAllQuestionsInCategory()) {
                this.setImeiOrSn(this.previousImeiOrSn);
                await this.fetchEstimatedPrice(this.language);
                return;
            }
            if (this.formData.imeiOrSn) {
                await this.fetchImeiValidityAndPrice(this.language);
                this.validateImeiResponse();
                if (((_a = this.imeiValidation.response) === null || _a === void 0 ? void 0 : _a.success) && this.imeiValidation.response.items.length) {
                    this.priceInfo.response = this.imeiValidation.response.items[0];
                }
                else {
                    await this.fetchEstimatedPrice(this.language);
                }
            }
            else {
                this.resetImeiValidation();
            }
        };
        this.onImeiFocus = () => {
            this.setImeiFocused(true);
            this.previousImeiOrSn = this.imeiOrSn;
        };
        this.onImeiBlur = async () => {
            if (this.hasImeiOrSnChanged) {
                await this.onImeiOrSnChanged();
                this.validateImeiInput();
            }
            this.setImeiFocused(false);
        };
        this.setSelectedCategoryId = (categoryId) => {
            this.formData.categoryId = Number(categoryId);
        };
        this.setSelectedManufacturerId = (manufacturer) => {
            this.selected.manufacturerId = Number(manufacturer.id);
        };
        this.setSelectedDeviceId = (device) => {
            this.formData.deviceId = Number(device.id);
        };
        this.filterManufacturers = (query) => {
            this.manufacturersSearchString = query;
        };
        this.filterDevices = (query) => {
            this.devicesSearchString = query;
        };
        this.setFormDataConditions = (key, value) => {
            const _conditions = [...this.formData.conditions];
            const index = _conditions.indexOf(key);
            if (index > -1) {
                _conditions.splice(index, 1);
            }
            if (!value) {
                _conditions.push(key);
            }
            this.formData.conditions = _conditions;
        };
        this.setImeiOrSn = (imeiOrSn) => {
            this.formData.imeiOrSn = imeiOrSn;
            this.previousImeiOrSn = '';
        };
        this.setQuestionAnswer = (key, value, condition) => {
            this.answeredQuestions.add(key);
            this.setFormDataConditions(condition, value === `question-yes-selector-${key}`);
        };
        this.setTerms = (terms) => {
            if (terms) {
                this.formErrors.delete('terms');
            }
            else {
                this.formErrors.set('terms', 'required');
            }
            this.terms = terms;
        };
        this.setImeiFocused = (focused) => {
            this.imeiFocused = focused;
        };
        this.tradeIn = async () => {
            var _a, _b;
            if (!this.isLoggedIn) {
                this.ensureLogin(null, () => setTimeout(() => this.tradeIn(), 0));
                return;
            }
            this.resetTradeInResponse();
            const formData = this.buildFormData(this.language);
            await this.submitAddTradeInToCart(formData);
            if (this.tradeInResponseHolder.status !== FetchStatus.ERROR) {
                this.finalPrice = (_b = (_a = this.priceInfo) === null || _a === void 0 ? void 0 : _a.response) === null || _b === void 0 ? void 0 : _b.price;
                this.setModalOpen(true);
                this.onSuccess();
                this.selected.manufacturerId = null;
                this.formData.imeiOrSn = '';
                this.formData.conditions = this.initialConditions;
                this.deviceLockConfirmed = false;
                this.dataConfirmationChecked = false;
                this.resetPriceInfo();
                this.resetImeiValidation();
            }
        };
        this.validateImeiInput = () => {
            if (!this.formData.imeiOrSn) {
                this.formErrors.set('imei', 'required');
            }
            else if (this.isPhone && this.formData.imeiOrSn.length < 15) {
                this.formErrors.set('imei', 'invalid');
            }
            else if (!this.imeiValidation.response || !this.imeiValidation.response.error) {
                this.clearImeiErrors();
            }
        };
        this.clearImeiErrors = () => {
            this.formErrors.delete('imei');
        };
        this.clearImeiValidationResponse = () => {
            this.imeiValidation = {
                status: FetchStatus.UNINITIALIZED,
                response: null,
                error: null,
            };
        };
        this.getConditionBulletPointImages = (condition, bulletpoint) => {
            switch (condition) {
                case Condition.SCREEN_GLASS_CRACKED:
                    switch (bulletpoint) {
                        case 1:
                            return [
                                ['mobile_cracked_03.jpg', 'error'],
                                ['mobile_camera_01.jpg', 'error'],
                            ];
                        case 3:
                            return [['frame_deformed.png', 'error']];
                        case 4:
                            return [
                                ['mobile_cracked_02.jpg', 'error'],
                                ['mobile_other_02.jpg', 'error'],
                            ];
                        default:
                            return [];
                    }
                case Condition.SCREEN_COLORS_OR_TOUCH_ISSUE:
                    switch (bulletpoint) {
                        case 1:
                            return [
                                ['mobile_pixel_01.jpg', 'error'],
                                ['mobile_pixel_03.jpg', 'error'],
                            ];
                        case 3:
                            return [
                                ['mobile_cracked_01.jpg', 'error'],
                                ['mobile_cracked_03.jpg', 'error'],
                            ];
                        case 4:
                            return [
                                ['mobile_burn_01.jpg', 'error'],
                                ['mobile_burn_03.jpg', 'error'],
                            ];
                        case 6:
                            return [
                                ['mobile_fold_01.png', 'error'],
                                ['mobile_flip_01.png', 'error'],
                            ];
                        default:
                            return [];
                    }
                case Condition.LAPTOP_NO_POWER:
                    switch (bulletpoint) {
                        case 1:
                            return [
                                ['laptop_boot_01.jpg', 'error'],
                                ['laptop_boot_02.jpg', 'success'],
                            ];
                        default:
                            return [];
                    }
                case Condition.LAPTOP_SCREEN_DAMAGE:
                    switch (bulletpoint) {
                        case 2:
                            return [['laptop_screen_01.jpg', 'error']];
                        case 3:
                            return [
                                ['laptop_screen_02.jpg', 'error'],
                                ['laptop_screen_03.jpg', 'error'],
                            ];
                        case 4:
                            return [['laptop_screen_04.jpg', 'error']];
                        default:
                            return [];
                    }
                case Condition.LAPTOP_PHYSICAL_DAMAGE:
                    switch (bulletpoint) {
                        case 1:
                            return [['laptop_screen_02.jpg', 'error']];
                        case 4:
                            return [['laptop_screen_03.jpg', 'error']];
                        case 7:
                            return [['laptop_crack_01.jpg', 'error']];
                        default:
                            return [];
                    }
                case Condition.SMARTWATCH_POWER_OR_SCREEN_DAMAGE:
                    switch (bulletpoint) {
                        case 3:
                            return [
                                ['watch_screenburn.png', 'error'],
                                ['watch_cracked.png', 'error'],
                            ];
                        case 4:
                            return [
                                ['watch_button.png', 'error'],
                                ['watch_loose.png', 'error'],
                            ];
                        default:
                            return [];
                    }
                default:
                    return [];
            }
        };
        this.closeTradeInModal = () => {
            this.setModalOpen(false);
        };
        this.getConditionBulletPoints = (condition) => {
            const getLabels = (n) => Array.from(Array(n).keys()).map((i) => `tradeIn.conditions.${condition}.${i + 1}`);
            switch (condition) {
                case Condition.SCREEN_GLASS_CRACKED:
                    return getLabels(5);
                case Condition.SCREEN_COLORS_OR_TOUCH_ISSUE:
                    return getLabels(6);
                case Condition.LAPTOP_NO_POWER:
                    return getLabels(5);
                case Condition.LAPTOP_SCREEN_DAMAGE:
                    return getLabels(4);
                case Condition.LAPTOP_BOOT_FAILS:
                    return getLabels(1);
                case Condition.LAPTOP_PHYSICAL_DAMAGE:
                    return getLabels(9);
                case Condition.SMARTWATCH_POWER_OR_SCREEN_DAMAGE:
                    return getLabels(4);
                case Condition.SMARTWATCH_CHARGER_AND_BAND:
                    return getLabels(4);
                default:
                    return [];
            }
        };
        this.getPromotedManufacturerSuggestions = (manufacturerByCategory, manufacturers) => {
            if (!manufacturerByCategory) {
                return [];
            }
            const promotedManufacturers = (manufacturers || []).filter((m) => manufacturerByCategory.some((man) => man.toLowerCase() === m.label.toLowerCase()));
            promotedManufacturers.sort((a, b) => manufacturerByCategory.indexOf(a.label.toLowerCase()) - manufacturerByCategory.indexOf(b.label.toLowerCase()));
            return promotedManufacturers;
        };
        this.buildFormData = (language) => {
            return {
                ...this.formData,
                lang: language.toUpperCase(),
                conditions: this.formData.conditions.length ? this.formData.conditions : [Condition.OK],
            };
        };
        this.orderCategories = (categories) => {
            const categoryIndexesOrdered = ['1', '12', '3', '4'];
            return categories.sort((categoryA, categoryB) => {
                const indexOfA = categoryIndexesOrdered.findIndex((orderIndex) => categoryA.value === orderIndex);
                const indexOfB = categoryIndexesOrdered.findIndex((orderIndex) => categoryB.value === orderIndex);
                return indexOfA - indexOfB;
            });
        };
        this.isOrderValid = () => {
            this.formErrors.clear();
            if (!this.formData.deviceId) {
                this.formErrors.set('deviceId', 'required');
            }
            if (!this.areConfirmationCheckboxesClicked) {
                this.formErrors.set('terms', 'required');
            }
            this.validateImeiInput();
            this.validateImeiResponse();
            return ((!this.formErrors.size || (this.formErrors.size === 1 && this.imeiIsNoMatch && this.correctDeviceConfirmationChecked)) &&
                this.deviceLockConfirmed &&
                this.dataConfirmationChecked &&
                (!this.correctDeviceConfirmationNeeded || this.correctDeviceConfirmationChecked));
        };
        this.fetchImeiValidityAndPrice = async (lang) => {
            var _a;
            this.imeiValidation.status = FetchStatus.FETCHING;
            if (this.isCellphoneWithInvalidImei()) {
                this.imeiValidation.response = {
                    error: 5,
                    items: [],
                    success: false,
                };
                this.imeiValidation.status = FetchStatus.FETCHED;
                return;
            }
            try {
                const { data } = await API(this.urlData.commonAPIBaseURL, {
                    timeout: DEFAULT_TIMEOUT * 1.5,
                }).post('/trade-in/validate-imei', this.buildFormData(lang));
                this.imeiValidation.response = data;
                this.imeiValidation.error = null;
                this.imeiValidation.status = FetchStatus.FETCHED;
            }
            catch (e) {
                this.imeiValidation.response = null;
                this.imeiValidation.error = (_a = e.response) === null || _a === void 0 ? void 0 : _a.data;
                this.imeiValidation.status = FetchStatus.ERROR;
            }
        };
        this.fetchMaximumPrice = async (lang) => {
            var _a;
            try {
                this.maximumPrice.status = FetchStatus.FETCHING;
                const { data } = await API(this.urlData.commonAPIBaseURL).post('/trade-in/estimated-price', {
                    ...this.formData,
                    lang: lang.toUpperCase(),
                    conditions: [Condition.OK],
                });
                this.maximumPrice.response = data;
                this.maximumPrice.status = FetchStatus.FETCHED;
            }
            catch (e) {
                this.maximumPrice.error = (_a = e.response) === null || _a === void 0 ? void 0 : _a.data;
                this.maximumPrice.status = FetchStatus.ERROR;
            }
        };
        this.fetchEstimatedPrice = async (lang) => {
            var _a;
            try {
                this.priceInfo.status = FetchStatus.FETCHING;
                const { data } = await API(this.urlData.commonAPIBaseURL).post('/trade-in/estimated-price', this.buildFormData(lang));
                this.priceInfo.status = FetchStatus.FETCHED;
                this.priceInfo.response = data;
            }
            catch (e) {
                this.priceInfo.error = (_a = e.response) === null || _a === void 0 ? void 0 : _a.data;
                this.priceInfo.status = FetchStatus.ERROR;
            }
        };
        this.fetchCategories = async () => {
            try {
                this.categoriesHolder.status = FetchStatus.FETCHING;
                const { data } = await API(this.urlData.commonAPIBaseURL).get('/trade-in/categories');
                this.categoriesHolder.categories = data.categories;
                this.categoriesHolder.status = FetchStatus.FETCHED;
            }
            catch (_e) {
                this.categoriesHolder.status = FetchStatus.ERROR;
            }
        };
        this.fetchManufacturers = async () => {
            try {
                this.manufacturersHolder.status = FetchStatus.FETCHING;
                const { data } = await API(this.urlData.commonAPIBaseURL).get(`/trade-in/manufacturers?categoryId=${this.formData.categoryId}`);
                this.manufacturersHolder.manufacturers.set(this.formData.categoryId, data.manufacturers);
                this.manufacturersHolder.status = FetchStatus.FETCHED;
            }
            catch (_e) {
                this.manufacturersHolder.status = FetchStatus.ERROR;
            }
        };
        this.fetchDevices = async () => {
            try {
                this.devicesHolder.status = FetchStatus.FETCHING;
                const { data } = await API(this.urlData.commonAPIBaseURL).get('/trade-in/devices', {
                    params: {
                        categoryId: this.formData.categoryId,
                        manufacturerId: this.selected.manufacturerId,
                        manufacturerName: this.selectedManufacturerName,
                    },
                });
                this.devicesHolder.devices.set(this.selected.manufacturerId, data.devices);
                this.devicesHolder.status = FetchStatus.FETCHED;
            }
            catch (_e) {
                this.devicesHolder.status = FetchStatus.ERROR;
            }
        };
        this.validateImeiResponse = () => {
            if (this.imeiValidation.response && !this.imeiValidation.response.success) {
                this.formErrors.set('imei', ValidationError[this.imeiValidation.response.error]);
            }
        };
        this.getErrorObject = (key, params, linkToCart) => ({
            key,
            params,
            linkToCart,
        });
        makeObservable(this);
        const { language, app, tier, ensureLogin, onSuccess, isLoggedIn, translate } = props;
        this.setLoggedIn(isLoggedIn);
        this.ensureLogin = ensureLogin;
        this.onSuccess = onSuccess;
        this.setLanguage(language);
        this.setUrlData(app, tier, language);
        this.setTranslate(translate);
    }
    async initialize() {
        this.reactions.push(reaction(() => this.formData.categoryId, () => this.onCategoryChanged()));
        this.reactions.push(reaction(() => this.selected.manufacturerId, () => this.onManufacturerChanged()));
        this.reactions.push(reaction(() => this.formData.conditions.length, () => this.onConditionsChanged(this.language)));
        this.reactions.push(reaction(() => this.formData.deviceId, () => this.onDeviceChanged()));
        await this.fetchCategories();
        this.selectInitialCategory();
    }
    setLoggedIn(loggedIn) {
        this.isLoggedIn = loggedIn;
    }
    setUrlData(app, tier, lang) {
        this.urlData = new URLData({ app: app, tier: tier, lang: lang });
    }
    async onCategoryChanged() {
        this.answeredQuestions.clear();
        this.questionsClosed.clear();
        this.selected.manufacturerId = null;
        this.manufacturersSearchString = '';
        this.imeiValidation.status = FetchStatus.UNINITIALIZED;
        this.imeiValidation.response = null;
        this.formData.imeiOrSn = null;
        this.formData.conditions = this.initialConditions;
        await this.fetchManufacturers();
    }
    async onManufacturerChanged() {
        this.answeredQuestions.clear();
        this.questionsClosed.clear();
        this.formData.deviceId = null;
        this.devicesSearchString = '';
        if (this.selected.manufacturerId) {
            this.formData.conditions = this.initialConditions;
            await this.fetchDevices();
        }
    }
    async onDeviceChanged() {
        if (this.imeiOrSn) {
            this.previousImeiOrSn = this.imeiOrSn;
        }
        this.formData.imeiOrSn = null;
        this.answeredQuestions.clear();
        this.questionsClosed.clear();
        this.formErrors.clear();
        this.terms = false;
        this.deviceLockConfirmed = false;
        this.dataConfirmationChecked = false;
        this.resetTradeInResponse();
        this.resetImeiValidation();
        if (this.formData.deviceId) {
            this.formData.conditions = this.initialConditions;
            await Promise.all([this.fetchEstimatedPrice(this.language), this.fetchMaximumPrice(this.language)]);
        }
    }
    hasAnsweredAllQuestionsInCategory() {
        return this.answeredQuestions.size === this.initialConditions.length;
    }
    getTooltipBefore(condition, bulletPointNr) {
        const labels = {
            label: `tradeIn.conditions.${condition}.${bulletPointNr}.tooltipBefore.label`,
            content: `tradeIn.conditions.${condition}.${bulletPointNr}.tooltipBefore.content`,
        };
        if (condition === Condition.LAPTOP_NO_POWER && bulletPointNr === 4) {
            return labels;
        }
    }
    getTooltipAfter(condition, bulletPointNr) {
        const labels = {
            label: `tradeIn.conditions.${condition}.${bulletPointNr}.tooltipAfter.label`,
            content: `tradeIn.conditions.${condition}.${bulletPointNr}.tooltipAfter.content`,
        };
        if (condition === Condition.SCREEN_COLORS_OR_TOUCH_ISSUE && (bulletPointNr === 1 || bulletPointNr === 4)) {
            return labels;
        }
    }
    setLanguage(lang) {
        this.language = lang;
    }
    get correctDeviceConfirmationNeeded() {
        var _a;
        return [ValidationError.NO_MATCH_MULTIPLE, ValidationError.NO_MATCH].includes((_a = this.imeiValidation.response) === null || _a === void 0 ? void 0 : _a.error);
    }
    get areConfirmationCheckboxesClicked() {
        return (!this.correctDeviceConfirmationNeeded || this.correctDeviceConfirmationChecked) && this.dataConfirmationChecked && this.deviceLockConfirmed;
    }
    get isModalOpen() {
        return this.modalOpen;
    }
    get categories() {
        var _a;
        return (((_a = this.categoriesHolder.categories) === null || _a === void 0 ? void 0 : _a.map((category) => {
            var _a;
            return ({
                id: String(category.id),
                label: `tradeIn.selectors.category.${(_a = category.name) === null || _a === void 0 ? void 0 : _a.toLowerCase()}`,
                value: String(category.id),
                name: category.name,
            });
        })) || []);
    }
    get manufacturersByCategory() {
        return (this.manufacturersHolder.manufacturers.get(this.formData.categoryId) || []).filter((manufacturer) => {
            return manufacturer.name.toLowerCase().includes(this.manufacturersSearchString.toLowerCase());
        });
    }
    get devicesByManufacturer() {
        return (this.devicesHolder.devices.get(this.selected.manufacturerId) || []).filter((device) => {
            return device.name.toLowerCase().includes(this.devicesSearchString.toLowerCase());
        });
    }
    get selectedDeviceId() {
        return this.formData.deviceId;
    }
    get selectedCategoryId() {
        return this.formData.categoryId;
    }
    get selectedCategory() {
        return this.categories.find((c) => c.value === String(this.formData.categoryId)) || this.emptySelectorValue;
    }
    get selectedManufacturer() {
        let manufacturersWithoutPopularGroup;
        const [, allManufacturers] = this.formattedManufacturers;
        if (allManufacturers === null || allManufacturers === void 0 ? void 0 : allManufacturers.hasOwnProperty('items')) {
            manufacturersWithoutPopularGroup = allManufacturers.items;
        }
        else {
            manufacturersWithoutPopularGroup = this.formattedManufacturers;
        }
        return manufacturersWithoutPopularGroup.find((m) => m.id === String(this.selected.manufacturerId)) || this.emptySelectValue;
    }
    get selectedManufacturerName() {
        return this.selectedManufacturer.label;
    }
    get formattedDevices() {
        return this.devicesByManufacturer
            .map((device) => ({
            ...device,
            name: device.name.replace(this.selectedManufacturer.label, '').trim(),
        }))
            .map(formatOptions);
    }
    get selectedDevice() {
        return this.formattedDevices.find((d) => d.id === String(this.selectedDeviceId)) || this.emptySelectValue;
    }
    get selectedDeviceName() {
        return this.selectedDevice.label;
    }
    get conditions() {
        return this.formData.conditions;
    }
    get imeiOrSn() {
        return this.formData.imeiOrSn || '';
    }
    get isAppleLaptop() {
        return this.formData.categoryId === LAPTOP_CATEGORY_ID && this.selected.manufacturerId === APPLE_ID;
    }
    get isPhone() {
        return this.formData.categoryId === PHONE_CATEGORY_ID;
    }
    get shopCategory() {
        if (this.formData.categoryId === LAPTOP_CATEGORY_ID) {
            return 'sulearvutid';
        }
        else if (this.formData.categoryId === TABLET_CATEGORY_ID) {
            return 'tahvelarvutid';
        }
        else if (this.formData.categoryId === SMARTWATCH_CATEGORY_ID) {
            return 'nutikellad';
        }
        return 'nutitelefonid';
    }
    get areManufacturersFetching() {
        return this.manufacturersHolder.status === FetchStatus.UNINITIALIZED || this.manufacturersHolder.status === FetchStatus.FETCHING;
    }
    get areCategoriesFetching() {
        return this.categoriesHolder.status === FetchStatus.UNINITIALIZED || this.categoriesHolder.status === FetchStatus.FETCHING;
    }
    get areDevicesFetching() {
        return this.devicesHolder.status === FetchStatus.UNINITIALIZED || this.devicesHolder.status === FetchStatus.FETCHING;
    }
    get isPriceFetching() {
        return this.priceInfo.status === FetchStatus.FETCHING;
    }
    get isMaximumPriceFetching() {
        return this.maximumPrice.status === FetchStatus.FETCHING;
    }
    get hasMaximumPriceFetchErrored() {
        return this.maximumPrice.status === FetchStatus.ERROR;
    }
    get isImeiFetching() {
        return this.imeiValidation.status === FetchStatus.FETCHING;
    }
    get isPriceUninitialized() {
        return this.priceInfo.status === FetchStatus.UNINITIALIZED;
    }
    get isImeiUninitialized() {
        return this.imeiValidation.status === FetchStatus.UNINITIALIZED;
    }
    get isPriceFetchingOrUninitialized() {
        return this.isPriceFetching || this.isPriceUninitialized;
    }
    get isImeiFetchingOrUninitialized() {
        return this.isImeiFetching || this.isImeiUninitialized;
    }
    get isTradeInSubmitting() {
        return this.tradeInResponseHolder.status === FetchStatus.FETCHING;
    }
    get formError() {
        return !![this.categoriesHolder.status, this.manufacturersHolder.status, this.devicesHolder.status, this.imeiValidation.status, this.priceInfo.status].find((status) => status === FetchStatus.ERROR);
    }
    get imeiValid() {
        var _a;
        return (!this.formErrors.get('imei') && ((_a = this.imeiValidation.response) === null || _a === void 0 ? void 0 : _a.success)) || this.imeiIsNoMatch;
    }
    get hasImeiOrSnChanged() {
        return this.imeiOrSn !== this.previousImeiOrSn;
    }
    get imeiIsNoMatch() {
        return [ValidationError[ValidationError.NO_MATCH_MULTIPLE], ValidationError[ValidationError.NO_MATCH]].includes(this.formErrors.get('imei'));
    }
    get initialConditions() {
        if (SMARTWATCH_CATEGORY_ID === this.selectedCategoryId) {
            return SMARTWATCH_INITIAL_CONDITION_OPTIONS;
        }
        if (isPhoneOrTablet(this.selectedCategoryId)) {
            return PHONE_AND_TABLET_INITIAL_CONDITION_OPTIONS;
        }
        return LAPTOP_INITIAL_CONDITION_OPTIONS;
    }
    get formattedManufacturers() {
        const manufacturers = this.manufacturersByCategory.map(formatOptions);
        if (this.manufacturersSearchString.length) {
            return manufacturers;
        }
        return this.addPromotedManufacturersGroup(manufacturers);
    }
    addPromotedManufacturersGroup(manufacturers) {
        const promotedManufacturers = this.promotedManufacturers[this.selectedCategory.name.toLowerCase()];
        const promotedManufacturersOptions = this.getPromotedManufacturerSuggestions(promotedManufacturers, manufacturers);
        if (!promotedManufacturersOptions.length) {
            return manufacturers;
        }
        const promoted = {
            label: this.translate('tradeIn.store.popular'),
            items: promotedManufacturersOptions,
        };
        const all = {
            label: this.translate('tradeIn.store.allManufacturers'),
            items: manufacturers,
        };
        return [promoted, all];
    }
    isCellphoneWithInvalidImei() {
        return this.formData.categoryId === PHONE_CATEGORY_ID && !validateImei(this.imeiOrSn);
    }
    onConditionsChanged(lang) {
        if (this.formData.imeiOrSn || (!!this.previousImeiOrSn && this.hasAnsweredAllQuestionsInCategory())) {
            this.onImeiOrSnChanged();
        }
        else if (this.formData.deviceId && this.answeredQuestions.size) {
            this.fetchEstimatedPrice(lang);
        }
    }
    selectInitialCategory() {
        this.formData.categoryId = this.categoriesHolder.categories[0].id;
    }
    async submitAddTradeInToCart(form) {
        try {
            this.tradeInResponseHolder.status = FetchStatus.FETCHING;
            const response = await ShopAPI(this.urlData.teliaShopURL).post('/cart/add-trade-in', form);
            this.tradeInResponseHolder.cart = response.data;
            this.tradeInResponseHolder.status = FetchStatus.FETCHED;
        }
        catch (e) {
            this.tradeInResponseHolder.status = FetchStatus.ERROR;
            this.tradeInResponseHolder.error = this.resolveErrorCode(e);
        }
    }
    resolveErrorCode(e) {
        var _a, _b, _c, _d;
        const errorBody = (_c = (_b = (_a = e.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.errors[0]) === null || _c === void 0 ? void 0 : _c.body;
        if (errorBody) {
            if (errorBody.includes('ajutiselt otsas')) {
                return this.getErrorObject('tradeIn.submit.error.outOfStock');
            }
            if (errorBody.includes('ei ole võimalik koos vanauueks soodustusega osta')) {
                return this.getErrorObject('tradeIn.submit.error.unavailable', { product: (_d = errorBody.match(/(Toodet) (.*) (ei)/)) === null || _d === void 0 ? void 0 : _d[2] }, true);
            }
            if (errorBody.includes('ostukorvis ei saa korraga olla mitut vana seadet')) {
                return this.getErrorObject('tradeIn.submit.error.multipleDevices');
            }
            if (errorBody.includes('ei ole kättesaadaval Omniva pakiautomaatis')) {
                return this.getErrorObject('tradeIn.submit.error.deliveryError');
            }
        }
        ErrorUtil.pushErrorToTeliaRumAndLog(e);
        return this.getErrorObject('general.error.message');
    }
    resetImeiValidation() {
        this.imeiValidation = {
            status: FetchStatus.UNINITIALIZED,
            response: null,
            error: null,
        };
    }
    resetPriceInfo() {
        this.priceInfo = {
            status: FetchStatus.UNINITIALIZED,
            response: null,
            error: null,
        };
    }
    resetTradeInResponse() {
        this.tradeInResponseHolder = {
            status: FetchStatus.UNINITIALIZED,
            cart: null,
            error: null,
        };
    }
    get emptySelectorValue() {
        return { value: '', name: '', id: '', label: '' };
    }
    get emptySelectValue() {
        return { id: '', label: '' };
    }
}
__decorate([
    observable
], TradeInStore.prototype, "tradeInResponseHolder", void 0);
__decorate([
    observable
], TradeInStore.prototype, "manufacturersHolder", void 0);
__decorate([
    observable
], TradeInStore.prototype, "devicesHolder", void 0);
__decorate([
    observable
], TradeInStore.prototype, "categoriesHolder", void 0);
__decorate([
    observable
], TradeInStore.prototype, "selected", void 0);
__decorate([
    observable
], TradeInStore.prototype, "maximumPrice", void 0);
__decorate([
    observable
], TradeInStore.prototype, "priceInfo", void 0);
__decorate([
    observable
], TradeInStore.prototype, "imeiValidation", void 0);
__decorate([
    observable
], TradeInStore.prototype, "formErrors", void 0);
__decorate([
    observable
], TradeInStore.prototype, "terms", void 0);
__decorate([
    observable
], TradeInStore.prototype, "loading", void 0);
__decorate([
    observable
], TradeInStore.prototype, "formData", void 0);
__decorate([
    observable
], TradeInStore.prototype, "manufacturersSearchString", void 0);
__decorate([
    observable
], TradeInStore.prototype, "devicesSearchString", void 0);
__decorate([
    observable
], TradeInStore.prototype, "imeiFocused", void 0);
__decorate([
    observable
], TradeInStore.prototype, "dataConfirmationChecked", void 0);
__decorate([
    observable
], TradeInStore.prototype, "correctDeviceConfirmationChecked", void 0);
__decorate([
    observable
], TradeInStore.prototype, "deviceLockConfirmed", void 0);
__decorate([
    observable
], TradeInStore.prototype, "answeredQuestions", void 0);
__decorate([
    observable
], TradeInStore.prototype, "questionsClosed", void 0);
__decorate([
    observable
], TradeInStore.prototype, "urlData", void 0);
__decorate([
    observable
], TradeInStore.prototype, "promotedManufacturers", void 0);
__decorate([
    observable
], TradeInStore.prototype, "finalPrice", void 0);
__decorate([
    observable
], TradeInStore.prototype, "previousImeiOrSn", void 0);
__decorate([
    observable
], TradeInStore.prototype, "modalOpen", void 0);
__decorate([
    observable
], TradeInStore.prototype, "language", void 0);
__decorate([
    observable
], TradeInStore.prototype, "isLoggedIn", void 0);
__decorate([
    observable
], TradeInStore.prototype, "translate", void 0);
__decorate([
    action
], TradeInStore.prototype, "initialize", null);
__decorate([
    action
], TradeInStore.prototype, "setPromotedManufacturers", void 0);
__decorate([
    action
], TradeInStore.prototype, "setDataConfirmationChecked", void 0);
__decorate([
    action
], TradeInStore.prototype, "setCorrectDeviceConfirmationChecked", void 0);
__decorate([
    action
], TradeInStore.prototype, "setDeviceLockConfirmation", void 0);
__decorate([
    action
], TradeInStore.prototype, "setLoggedIn", null);
__decorate([
    action
], TradeInStore.prototype, "setUrlData", null);
__decorate([
    action
], TradeInStore.prototype, "setModalOpen", void 0);
__decorate([
    action
], TradeInStore.prototype, "setTranslate", void 0);
__decorate([
    action
], TradeInStore.prototype, "onCategoryChanged", null);
__decorate([
    action
], TradeInStore.prototype, "onManufacturerChanged", null);
__decorate([
    action
], TradeInStore.prototype, "onDeviceChanged", null);
__decorate([
    action
], TradeInStore.prototype, "onImeiOrSnChanged", void 0);
__decorate([
    action
], TradeInStore.prototype, "onImeiFocus", void 0);
__decorate([
    action
], TradeInStore.prototype, "onImeiBlur", void 0);
__decorate([
    action
], TradeInStore.prototype, "setSelectedCategoryId", void 0);
__decorate([
    action
], TradeInStore.prototype, "setSelectedManufacturerId", void 0);
__decorate([
    action
], TradeInStore.prototype, "setSelectedDeviceId", void 0);
__decorate([
    action
], TradeInStore.prototype, "filterManufacturers", void 0);
__decorate([
    action
], TradeInStore.prototype, "filterDevices", void 0);
__decorate([
    action
], TradeInStore.prototype, "setFormDataConditions", void 0);
__decorate([
    action
], TradeInStore.prototype, "setImeiOrSn", void 0);
__decorate([
    action
], TradeInStore.prototype, "setQuestionAnswer", void 0);
__decorate([
    action
], TradeInStore.prototype, "setTerms", void 0);
__decorate([
    action
], TradeInStore.prototype, "setImeiFocused", void 0);
__decorate([
    action
], TradeInStore.prototype, "tradeIn", void 0);
__decorate([
    action
], TradeInStore.prototype, "validateImeiInput", void 0);
__decorate([
    action
], TradeInStore.prototype, "clearImeiErrors", void 0);
__decorate([
    action
], TradeInStore.prototype, "clearImeiValidationResponse", void 0);
__decorate([
    action
], TradeInStore.prototype, "closeTradeInModal", void 0);
__decorate([
    action
], TradeInStore.prototype, "getConditionBulletPoints", void 0);
__decorate([
    action
], TradeInStore.prototype, "setLanguage", null);
__decorate([
    computed
], TradeInStore.prototype, "correctDeviceConfirmationNeeded", null);
__decorate([
    computed
], TradeInStore.prototype, "areConfirmationCheckboxesClicked", null);
__decorate([
    computed
], TradeInStore.prototype, "isModalOpen", null);
__decorate([
    computed
], TradeInStore.prototype, "categories", null);
__decorate([
    computed
], TradeInStore.prototype, "manufacturersByCategory", null);
__decorate([
    computed
], TradeInStore.prototype, "devicesByManufacturer", null);
__decorate([
    computed
], TradeInStore.prototype, "selectedDeviceId", null);
__decorate([
    computed
], TradeInStore.prototype, "selectedCategoryId", null);
__decorate([
    computed
], TradeInStore.prototype, "selectedCategory", null);
__decorate([
    computed
], TradeInStore.prototype, "selectedManufacturer", null);
__decorate([
    computed
], TradeInStore.prototype, "selectedManufacturerName", null);
__decorate([
    computed
], TradeInStore.prototype, "formattedDevices", null);
__decorate([
    computed
], TradeInStore.prototype, "selectedDevice", null);
__decorate([
    computed
], TradeInStore.prototype, "selectedDeviceName", null);
__decorate([
    computed
], TradeInStore.prototype, "conditions", null);
__decorate([
    computed
], TradeInStore.prototype, "imeiOrSn", null);
__decorate([
    computed
], TradeInStore.prototype, "isAppleLaptop", null);
__decorate([
    computed
], TradeInStore.prototype, "isPhone", null);
__decorate([
    computed
], TradeInStore.prototype, "shopCategory", null);
__decorate([
    computed
], TradeInStore.prototype, "areManufacturersFetching", null);
__decorate([
    computed
], TradeInStore.prototype, "areCategoriesFetching", null);
__decorate([
    computed
], TradeInStore.prototype, "areDevicesFetching", null);
__decorate([
    computed
], TradeInStore.prototype, "isPriceFetching", null);
__decorate([
    computed
], TradeInStore.prototype, "isMaximumPriceFetching", null);
__decorate([
    computed
], TradeInStore.prototype, "hasMaximumPriceFetchErrored", null);
__decorate([
    computed
], TradeInStore.prototype, "isImeiFetching", null);
__decorate([
    computed
], TradeInStore.prototype, "isPriceUninitialized", null);
__decorate([
    computed
], TradeInStore.prototype, "isImeiUninitialized", null);
__decorate([
    computed
], TradeInStore.prototype, "isPriceFetchingOrUninitialized", null);
__decorate([
    computed
], TradeInStore.prototype, "isImeiFetchingOrUninitialized", null);
__decorate([
    computed
], TradeInStore.prototype, "isTradeInSubmitting", null);
__decorate([
    computed
], TradeInStore.prototype, "formError", null);
__decorate([
    computed
], TradeInStore.prototype, "imeiValid", null);
__decorate([
    computed
], TradeInStore.prototype, "hasImeiOrSnChanged", null);
__decorate([
    computed
], TradeInStore.prototype, "imeiIsNoMatch", null);
__decorate([
    computed
], TradeInStore.prototype, "initialConditions", null);
__decorate([
    computed
], TradeInStore.prototype, "formattedManufacturers", null);
export default TradeInStore;
